import { ArrowPathIcon, CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TotpSetup } from '@/api'
import { Dialog } from '@/components/Dialog'
import { TextField } from '@/components/TextField'

export type AuthenticatorDialogState = { mode: 'setup'; setup: TotpSetup } | { mode: 'manage' }

type AuthenticatorDialogProps = {
    state: AuthenticatorDialogState
    onClose: () => void
    onVerify: (code: string) => Promise<void>
    onDisable: () => Promise<void>
}

export const AuthenticatorDialog = ({ state, onClose, onVerify, onDisable }: AuthenticatorDialogProps) => {
    const { t } = useTranslation()

    const codeInputRef = useRef<HTMLInputElement | null>(null)
    const cancelRef = useRef<HTMLButtonElement | null>(null)

    const [copied, setCopied] = useState(false)
    const [code, setCode] = useState('')
    const [confirming, setConfirming] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    let title = t('security.local.authenticator.title')
    if (state.mode === 'setup') {
        title = t('security.local.authenticator.dialog.setup.title')
    } else if (confirming) {
        title = t('security.local.authenticator.dialog.disable.title')
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        setError(null)

        try {
            if (state.mode === 'setup') {
                await onVerify(code)
            } else {
                await onDisable()
            }
        } catch (cause) {
            let fallback = t('security.local.authenticator.messages.disableError')

            if (state.mode === 'setup') {
                fallback = t('security.local.authenticator.messages.verifyError')
            }

            setError(cause instanceof Error ? cause.message : fallback)
        } finally {
            setSubmitting(false)
        }
    }

    const handleCopy = async () => {
        if (state.mode !== 'setup') {
            return
        }

        await navigator.clipboard.writeText(state.setup.secret)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
    }

    return (
        <Dialog
            open
            title={title}
            onClose={onClose}
            closeLabel={t('common.close')}
            dismissible={!submitting}
            initialFocusRef={state.mode === 'setup' ? codeInputRef : cancelRef}
            footer={
                <div className="flex flex-wrap justify-end gap-3">
                    <button
                        ref={cancelRef}
                        type="button"
                        disabled={submitting}
                        onClick={() => {
                            if (confirming) {
                                setConfirming(false)
                                setError(null)
                            } else {
                                onClose()
                            }
                        }}
                        className="border-account-border bg-account-surface text-account-primary hover:bg-account-muted rounded-lg border px-4 py-2 font-semibold disabled:opacity-60"
                    >
                        {state.mode === 'setup' || confirming ? t('common.cancel') : t('common.close')}
                    </button>
                    {state.mode === 'setup' ? (
                        <button
                            type="button"
                            onClick={() => {
                                void handleSubmit()
                            }}
                            disabled={submitting || !code}
                            className="bg-account-accent text-account-on-accent hover:bg-account-accent-hover rounded-lg px-6 py-2 font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t('security.local.authenticator.dialog.setup.submit')}
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={submitting}
                            onClick={() => {
                                if (confirming) {
                                    void handleSubmit()
                                } else {
                                    setConfirming(true)
                                    cancelRef.current?.focus()
                                }
                            }}
                            className="border-account-border text-account-error hover:bg-account-error-soft inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting && <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />}
                            {t('security.local.authenticator.dialog.disable.action')}
                        </button>
                    )}
                </div>
            }
        >
            {state.mode === 'setup' ? (
                <>
                    <p className="text-account-secondary text-sm">
                        {t('security.local.authenticator.dialog.setup.description')}
                    </p>
                    <div className="flex justify-center">
                        <div className="border-account-divider rounded-lg border bg-white p-3">
                            <img src={state.setup.qrCodeDataUrl} alt="" className="h-40 w-40" />
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="bg-account-muted text-account-secondary inline-flex max-w-full items-center gap-2 rounded-lg px-3 py-2 text-sm">
                            <code className="text-account-primary min-w-0 truncate font-mono tracking-[0.18em]">
                                {state.setup.secret}
                            </code>
                            <button
                                type="button"
                                onClick={() => {
                                    void handleCopy()
                                }}
                                aria-label={
                                    copied
                                        ? t('security.local.authenticator.dialog.setup.copied')
                                        : t('security.local.authenticator.dialog.setup.copy')
                                }
                                title={
                                    copied
                                        ? t('security.local.authenticator.dialog.setup.copied')
                                        : t('security.local.authenticator.dialog.setup.copy')
                                }
                                className="text-account-secondary hover:text-account-primary hover:bg-account-surface inline-flex shrink-0 items-center rounded-md p-1.5 transition"
                            >
                                {copied ? (
                                    <CheckIcon className="text-account-success h-4 w-4" />
                                ) : (
                                    <DocumentDuplicateIcon className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    </div>
                    <TextField
                        inputRef={codeInputRef}
                        label={t('security.local.authenticator.dialog.setup.codeLabel')}
                        value={code}
                        placeholder="000000"
                        centered
                        onChange={(value) => {
                            setCode(value)
                            setError(null)
                        }}
                        error={error}
                    />
                </>
            ) : (
                <>
                    {!confirming && (
                        <p className="text-account-success text-sm font-medium">{t('security.local.status.enabled')}</p>
                    )}
                    <p className="text-account-secondary text-sm">
                        {confirming
                            ? t('security.local.authenticator.dialog.disable.description')
                            : t('security.local.authenticator.dialog.manage.description')}
                    </p>
                    {error && (
                        <p role="alert" className="text-account-error text-sm">
                            {error}
                        </p>
                    )}
                </>
            )}
        </Dialog>
    )
}
